export namespace DateTime {
    export const DateTimeFormatCharacter = {
        Year: 'y',
        Month: 'M',
        Day: 'd'
    };
    export function copyDate(src: Date, dest: Date) {
        const date = new Date(
            src.getFullYear(),
            src.getMonth(),
            src.getDate(),
            dest.getHours(),
            dest.getMinutes(),
            dest.getSeconds(),
            dest.getMilliseconds()
        );
        dest.setTime(date.getTime());
    }
    export function copyTime(src: Date, dest: Date) {
        const date = new Date(
            dest.getFullYear(),
            dest.getMonth(),
            dest.getDate(),
            src.getHours(),
            src.getMinutes(),
            src.getSeconds(),
            src.getMilliseconds()
        );
        dest.setTime(date.getTime());
    }

    export function isEqual(d1: Date, d2: Date): boolean {
        const d1IsNull = MsftSme.isNullOrUndefined(d1);
        const d2IsNull = MsftSme.isNullOrUndefined(d2);
        if (d1 === d2 || (d1IsNull && d2IsNull)) {
            return true;
        }
        if (d1IsNull !== d2IsNull) {
            return false;
        }
        return d1.getTime() === d2.getTime();
    }

    export function isEqualDates(d1: Date, d2: Date): boolean {
        const d1IsNull = MsftSme.isNullOrUndefined(d1);
        const d2IsNull = MsftSme.isNullOrUndefined(d2);
        if (d1 === d2 || (d1IsNull && d2IsNull)) {
            return true;
        }
        if (d1IsNull !== d2IsNull) {
            return false;
        }
        return d1.getFullYear() === d2.getFullYear()
            && d1.getMonth() === d2.getMonth()
            && d1.getDate() === d2.getDate();
    }

    export function isEqualTimes(d1: Date, d2: Date): boolean {
        const d1IsNull = MsftSme.isNullOrUndefined(d1);
        const d2IsNull = MsftSme.isNullOrUndefined(d2);

        if (d1 === d2 || (d1IsNull && d2IsNull)) {
            return true;
        }
        if (d1IsNull !== d2IsNull) {
            return false;
        }
        return d1.getHours() === d2.getHours()
            && d1.getMinutes() === d2.getMinutes()
            && d1.getSeconds() === d2.getSeconds()
            && d1.getUTCMilliseconds() === d2.getUTCMilliseconds();
    }

    export function compareDateTimes(d1: Date, d2: Date): 1 | 0 | -1 {
        const d1IsNull = MsftSme.isNullOrUndefined(d1);
        const d2IsNull = MsftSme.isNullOrUndefined(d2);

        if (d1 === d2 || (d1IsNull && d2IsNull)) {
            return 0;
        }
        if (d1IsNull && !d2IsNull) {
            return -1;
        }
        if (!d1IsNull && d2IsNull) {
            return 1;
        }

        return d1 > d2 ? 1 : d1 < d2 ? -1 : 0;
    }

    export function compareDates(d1: Date, d2: Date): 1 | 0 | -1 {
        const d1IsNull = MsftSme.isNullOrUndefined(d1);
        const d2IsNull = MsftSme.isNullOrUndefined(d2);

        if (d1 === d2 || (d1IsNull && d2IsNull)) {
            return 0;
        }
        if (d1IsNull && !d2IsNull) {
            return -1;
        }
        if (!d1IsNull && d2IsNull) {
            return 1;
        }

        const d1DateOnly = DateTime.getDateOnly(d1);
        const d2DateOnly = DateTime.getDateOnly(d2);
        return DateTime.compareDateTimes(d1DateOnly, d2DateOnly);
    }

    export function compareTimes(d1: Date, d2: Date): 1 | 0 | -1 {
        const d1IsNull = MsftSme.isNullOrUndefined(d1);
        const d2IsNull = MsftSme.isNullOrUndefined(d2);

        if (d1 === d2 || (d1IsNull && d2IsNull)) {
            return 0;
        }
        if (d1IsNull && !d2IsNull) {
            return -1;
        }
        if (!d1IsNull && d2IsNull) {
            return 1;
        }

        const d1TimeOnly = DateTime.getTimeOnly(d1);
        const d2TimeOnly = DateTime.getTimeOnly(d2);
        return DateTime.compareDateTimes(d1TimeOnly, d2TimeOnly);
    }

    export function getDateOnly(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    export function getTimeOnly(date: Date, includeSeconds: boolean = false, includeMilliseconds: boolean = false): Date {
        if (includeMilliseconds) {
            return new Date(0, 0, 0, date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
        } else if (includeSeconds) {
            return new Date(0, 0, 0, date.getHours(), date.getMinutes(), date.getSeconds(), 0);
        } else {
            return new Date(0, 0, 0, date.getHours(), date.getMinutes(), 0, 0);
        }
    }

    export function getLastDayOfMonth(year: number, month: number): Date {
        return new Date(year, month + 1, 0);
    }

    export function getLastDayOfPreviousMonth(year: number, month: number): Date {
        const date = new Date(year, month, 1);
        date.setDate(date.getDate() - 1);
        return date;
    }

    export function getFirstDayOfNextMonth(year: number, month: number): Date {
        const date = new Date(year, month, this.getLastDayOfMonth(year, month).getDate());
        date.setDate(date.getDate() + 1);
        return date;
    }

    export function getLocaleDateStringFormat(localeId) {
        enum FormatPartType {
            Day = 'day',
            Month = 'month',
            Year = 'year',
            Literal = 'literal'
        }
        let dateFormat = '';
        const formatter = new Intl.DateTimeFormat(localeId);
        const sampleDate = new Date('2/2/2020');
        const parts = formatter.formatToParts(sampleDate);
        for (const part of parts) {
            switch (part.type) {
                case FormatPartType.Literal:
                    dateFormat += part.value;
                    break;
                case FormatPartType.Day:
                    dateFormat += DateTimeFormatCharacter.Day.repeat(part.value.length);
                    break;
                case FormatPartType.Month:
                    dateFormat += DateTimeFormatCharacter.Month.repeat(part.value.length);
                    break;
                case FormatPartType.Year:
                    dateFormat += DateTimeFormatCharacter.Year.repeat(part.value.length);
            }
        }
        return dateFormat;
    }
}
